import type { CaseStudy } from '../types';

export const taobaoVillage: CaseStudy = {
  id: 'taobao-village',
  order: 19,
  title: '淘宝村',
  english: 'Taobao Villages',
  category: 'business',
  era: '2009 — 今',
  region: '中国 / 县域与乡村',
  accent: '#c65a3e',
  hook: '一个村庄把家庭作坊、平台流量和快递网络接起来，可能形成新的收入来源；但同质化竞争和平台规则也会把利润压薄。',
  question: '平台让普通人更容易做生意，但为什么有的村庄形成产业，有的只留下低价竞争？',
  summary:
    '淘宝村把家庭作坊、电商平台、快递物流与本地供应链连接起来，降低了农户和小微企业进入全国市场的门槛。进入门槛低也意味着复制快、价格竞争激烈，收入分配高度不均。',
  thesis: '平台降低了交易成本，但不会自动创造利润；能不能把流量变成可持续收入，取决于本地供应链、差异化与组织能力。',
  quote: '平台给你一条通往全国市场的路，但没有保证路上有利润。',
  isNew: true,
  entryNodeIds: ['tb-platform', 'tb-cluster', 'tb-logistics'],
  focusNodeId: 'tb-cluster',
  lensIds: ['network', 'scale', 'public-goods', 'regulation', 'standards'],
  opportunityIds: ['opp-rural-ecommerce'],
  sourceIds: ['aliresearch-taobao-village'],
  facts: [
    {
      text: '阿里研究院通常将“淘宝村”定义为：活跃网店数量达到当地家庭户数 10% 以上，或活跃网店数量达到 100 家以上，且全年电商销售额达到一定规模（常见口径为 1000 万元人民币）。',
      sourceIds: ['aliresearch-taobao-village'],
    },
    {
      text: '淘宝村在全国多地出现，常见模式包括家庭作坊自产自销、为其他商家代工、以及围绕电商的物流、包装、摄影与运营服务。',
      sourceIds: ['aliresearch-taobao-village'],
    },
    {
      text: '“淘宝村”是平台研究机构的口径，不是官方行政区划或独立统计；不同报告年度的数量、认定标准与样本可能变化。',
      sourceIds: ['aliresearch-taobao-village'],
    },
  ],
  nodes: [
    {
      id: 'taobao-village',
      caseId: 'taobao-village',
      kind: 'case',
      stage: 0,
      lane: 0,
      title: '淘宝村',
      summary: '平台、家庭作坊与快递网络在县域形成的新产业组织。',
      detail:
        '淘宝村不是“农村开网店”这么简单。它通常需要本地有可生产或可采购的货源、愿意尝试的年轻人、能覆盖到村的快递、以及平台上的流量与信任。一个村庄形成集群后，包装、摄影、运营、客服和物流也会跟着出现。',
      boundary: '本案例讨论一种平台驱动的乡村产业组织，不代表所有农村电商都能成功，也不把“开店”等同于“赚钱”。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['aliresearch-taobao-village'],
    },
    {
      id: 'tb-platform',
      caseId: 'taobao-village',
      kind: 'mechanism',
      stage: 1,
      lane: -1,
      title: '平台：把本地供给接到全国需求',
      summary: '搜索、评价、支付与物流接口降低了跨地域交易的不确定性。',
      detail:
        '在传统批发体系里，乡村小作坊很难接触全国消费者。平台提供了展示、支付、信用评价和订单管理，把“找到买家”的成本大幅降低。流量分配与规则也由此成为新的稀缺资源。',
      boundary: '平台流量不是无限的；规则变化、广告成本上升和竞争加剧会迅速改变商户的回报。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['aliresearch-taobao-village'],
    },
    {
      id: 'tb-cluster',
      caseId: 'taobao-village',
      kind: 'mechanism',
      stage: 1,
      lane: 1,
      title: '集群：一家店带出一整条链',
      summary: '同村或邻近的作坊、包装、摄影、运营与快递共享订单与知识。',
      detail:
        '当村里有人跑通网店模式，亲友和邻居会模仿，供应商与快递也会进入。集群降低了采购、打样、包装和发货的协调成本，也让经验与订单在本地流动。但模仿也会带来同质化和价格战。',
      boundary: '集群需要足够的本地供给、物流密度和信任网络；如果只靠低价复制，很难升级。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['aliresearch-taobao-village'],
    },
    {
      id: 'tb-logistics',
      caseId: 'taobao-village',
      kind: 'mechanism',
      stage: 2,
      lane: -1,
      title: '物流：最后一公里的成本革命',
      summary: '快递网点进村、件量密度提高，让单件发货成本下降。',
      detail:
        '电商订单把分散的农户需求聚合成可运输的件量。快递网点、集包、共同配送和道路条件改善，降低了单件物流成本。物流是淘宝村能成立的关键互补资产：没有它，平台流量无法变成可交付的订单。',
      boundary: '偏远地区的物流成本仍高；退货、时效和破损会侵蚀利润。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['aliresearch-taobao-village'],
    },
    {
      id: 'tb-family',
      caseId: 'taobao-village',
      kind: 'mechanism',
      stage: 2,
      lane: 1,
      title: '家庭作坊：低固定成本，也低抗风险',
      summary: '用自家房屋、家庭成员和灵活工时降低启动成本，但难以规模化与合规。',
      detail:
        '家庭作坊的优势是固定成本低、决策快、愿意在旺季加班。劣势是产能有限、质量不稳定、缺少品牌与合规能力。很多淘宝村的收入来自“家庭劳动 + 平台订单”的组合，利润对价格和退货率非常敏感。',
      boundary: '当订单规模扩大，家庭作坊会遇到用工、税务、消防、环保与质量标准的约束。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['aliresearch-taobao-village'],
    },
    {
      id: 'tb-condition',
      caseId: 'taobao-village',
      kind: 'condition',
      stage: 2,
      lane: 0,
      title: '前提：货源、年轻人与基础设施',
      summary: '本地可生产/采购的货源、愿意尝试的年轻人、道路与快递，是村庄电商的起点。',
      detail:
        '不是每个村庄都有适合电商的货源。家具、服装、农产品、手工艺品等品类对物流、退货和展示的要求不同。返乡青年、电商培训、宽带与快递网点，决定了村庄能否从“有人开店”走向“形成集群”。',
      boundary: '政策补贴可以短期催生网店，但如果没有持续需求与供应链，补贴退出后可能大量消失。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['aliresearch-taobao-village'],
    },
    {
      id: 'tb-outcome',
      caseId: 'taobao-village',
      kind: 'outcome',
      stage: 3,
      lane: 0,
      title: '结果：收入增加与分化',
      summary: '一部分家庭获得新收入，另一部分在低价竞争、流量成本和退货中挣扎。',
      detail:
        '淘宝村确实为部分农户和小微企业创造了全国市场与就业，但收入分配高度不均：头部商家、供应链服务商和房东受益更多，普通店主可能只能赚取微薄加工费。平台流量、价格战和退货率决定谁能留下。',
      boundary: '不同村庄、品类与年份差异很大；不能用个别致富故事代表整体。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['aliresearch-taobao-village'],
    },
    {
      id: 'tb-risk',
      caseId: 'taobao-village',
      kind: 'risk',
      stage: 4,
      lane: -1,
      title: '脆弱处：低门槛带来快速复制',
      summary: '同质化、平台规则变化与价格战，会把利润压到最薄。',
      detail:
        '一个爆款出现后，邻居很快模仿，价格战随之而来。平台调整流量规则或广告成本上升，会让没有品牌和私域的商家更脆弱。家庭作坊的合规、质量与用工风险，也会在规模扩大后集中暴露。',
      boundary: '差异化产品、品牌、设计、服务与本地供应链组织，可以缓解但不能消除竞争。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['aliresearch-taobao-village'],
    },
    {
      id: 'tb-opp',
      caseId: 'taobao-village',
      kind: 'opportunity',
      stage: 4,
      lane: 1,
      title: '机会透镜：做集群的“共享能力”',
      summary: '当家家开店，真正稀缺的是设计、摄影、合规、包装、物流与渠道运营。',
      detail:
        '机会不一定再开一家网店，而是为集群提供共享能力：产品拍摄与详情页、包装与品牌、质检与合规、快递集包、跨境渠道、直播与私域运营。把单个家庭作坊做不了的事，做成按次或按月收费的服务。',
      boundary: '服务同样面临低价竞争与客户流失；只有当你能持续带来订单、降低成本或降低风险时，才有议价权。',
      evidence: 'inference',
      confidence: 'medium',
      sourceIds: ['aliresearch-taobao-village'],
    },
  ],
  edges: [
    {
      id: 'tb-e1',
      caseId: 'taobao-village',
      from: 'taobao-village',
      to: 'tb-platform',
      label: '接入全国市场',
      explanation: '平台把本地小作坊的商品展示给全国消费者，降低找客户的成本。',
      tone: 'cause',
    },
    {
      id: 'tb-e2',
      caseId: 'taobao-village',
      from: 'taobao-village',
      to: 'tb-cluster',
      label: '模仿与分工',
      explanation: '一家店跑通后，亲友模仿，包装、摄影、运营与快递逐步本地化。',
      tone: 'cause',
    },
    {
      id: 'tb-e3',
      caseId: 'taobao-village',
      from: 'tb-platform',
      to: 'tb-logistics',
      label: '订单聚合成件量',
      explanation: '平台订单让分散的乡村需求集中，快递网点与共同配送变得可行。',
      tone: 'enable',
    },
    {
      id: 'tb-e4',
      caseId: 'taobao-village',
      from: 'tb-cluster',
      to: 'tb-logistics',
      label: '集群降低发件成本',
      explanation: '同村件量密度提高，快递可以更频繁、更便宜地收件。',
      tone: 'cause',
    },
    {
      id: 'tb-e5',
      caseId: 'taobao-village',
      from: 'tb-cluster',
      to: 'tb-family',
      label: '订单进入家庭',
      explanation: '平台订单由家庭作坊承接，用自家房屋和家庭成员降低固定成本。',
      tone: 'cause',
    },
    {
      id: 'tb-e6',
      caseId: 'taobao-village',
      from: 'tb-logistics',
      to: 'tb-outcome',
      label: '可交付的订单',
      explanation: '低成本物流把平台流量变成可完成的交易，扩大市场半径。',
      tone: 'cause',
    },
    {
      id: 'tb-e7',
      caseId: 'taobao-village',
      from: 'tb-family',
      to: 'tb-outcome',
      label: '低成本供给',
      explanation: '家庭劳动与灵活工时为村庄电商提供了低门槛供给。',
      tone: 'cause',
    },
    {
      id: 'tb-e8',
      caseId: 'taobao-village',
      from: 'tb-condition',
      to: 'tb-outcome',
      label: '本地前提',
      explanation: '货源、年轻人、道路与快递决定村庄能否从开店走向集群。',
      tone: 'enable',
    },
    {
      id: 'tb-e9',
      caseId: 'taobao-village',
      from: 'tb-outcome',
      to: 'tb-risk',
      label: '低门槛的反面',
      explanation: '成功模式被快速复制，价格战与平台规则变化压缩利润。',
      tone: 'risk',
    },
    {
      id: 'tb-e10',
      caseId: 'taobao-village',
      from: 'tb-risk',
      to: 'tb-opp',
      label: '共享能力稀缺',
      explanation: '当人人开店，设计、合规、物流与运营等共享服务更有价值。',
      tone: 'enable',
    },
  ],
};
